/**
 * Pfadfindung auf dem Tile-Raster fuer Click-to-Move. Reine Funktionen ohne
 * Pixi/DOM — dadurch vollstaendig unit-testbar. Die Bewegung selbst laeuft
 * ueber den bestehenden LocalPlayer (Speed-Limit, Slide, Zutrittssperren),
 * der Pfad liefert nur die Wegpunkte.
 */

export interface TilePoint {
  x: number;
  y: number;
}

/** Abstraktion ueber CollisionGrid + verbotene Zonen — testbar ohne MapData. */
export interface PathGrid {
  width: number;
  height: number;
  isBlocked(tileX: number, tileY: number): boolean;
}

const NEIGHBORS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/**
 * Breitensuche (4 Richtungen) vom Start zum Ziel. Rueckgabe: Tile-Liste
 * inklusive Start und Ziel, oder null wenn kein Pfad existiert. Bei ~2000
 * Tiles ist BFS optimal und trivial; Diagonalen entstehen durchs Smoothing.
 */
export function findPath(grid: PathGrid, start: TilePoint, goal: TilePoint): TilePoint[] | null {
  const { width, height } = grid;
  const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height;
  if (!inBounds(start.x, start.y) || !inBounds(goal.x, goal.y)) return null;
  if (grid.isBlocked(goal.x, goal.y) || grid.isBlocked(start.x, start.y)) return null;
  if (start.x === goal.x && start.y === goal.y) return [{ x: start.x, y: start.y }];

  const idx = (x: number, y: number): number => y * width + x;
  const parent = new Int32Array(width * height).fill(-1);
  const visited = new Uint8Array(width * height);
  const queue: number[] = [idx(start.x, start.y)];
  visited[idx(start.x, start.y)] = 1;
  const goalIdx = idx(goal.x, goal.y);

  for (let head = 0; head < queue.length; head += 1) {
    const cur = queue[head]!;
    if (cur === goalIdx) break;
    const cx = cur % width;
    const cy = Math.floor(cur / width);
    for (const [dx, dy] of NEIGHBORS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (!inBounds(nx, ny)) continue;
      const nIdx = idx(nx, ny);
      if (visited[nIdx] || grid.isBlocked(nx, ny)) continue;
      visited[nIdx] = 1;
      parent[nIdx] = cur;
      queue.push(nIdx);
    }
  }

  if (!visited[goalIdx]) return null;
  const path: TilePoint[] = [];
  for (let cur = goalIdx; cur !== -1; cur = parent[cur]!) {
    path.push({ x: cur % width, y: Math.floor(cur / width) });
  }
  return path.reverse();
}

/**
 * Naechstes begehbares Tile im Umkreis (euklidisch sortiert), z. B. wenn der
 * Klick auf eine Wand/ein Moebelstueck/eine gesperrte Zone faellt. null =
 * keins gefunden.
 */
export function nearestWalkableTile(grid: PathGrid, target: TilePoint, maxRadius = 2): TilePoint | null {
  if (!grid.isBlocked(target.x, target.y)) return { x: target.x, y: target.y };
  const candidates: Array<{ p: TilePoint; d: number }> = [];
  for (let dy = -maxRadius; dy <= maxRadius; dy += 1) {
    for (let dx = -maxRadius; dx <= maxRadius; dx += 1) {
      if (dx === 0 && dy === 0) continue;
      const x = target.x + dx;
      const y = target.y + dy;
      if (x < 0 || y < 0 || x >= grid.width || y >= grid.height) continue;
      if (grid.isBlocked(x, y)) continue;
      candidates.push({ p: { x, y }, d: dx * dx + dy * dy });
    }
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.d - b.d);
  return candidates[0]!.p;
}

/**
 * String-Pulling: entfernt Zwischenpunkte, zwischen denen eine freie Sichtlinie
 * besteht — aus dem treppenfoermigen BFS-Pfad werden natuerliche Diagonalen.
 * `isSegmentClear` prueft eine gerade Strecke (kontinuierliche Koordinaten).
 */
export function smoothPath(
  path: readonly TilePoint[],
  isSegmentClear: (ax: number, ay: number, bx: number, by: number) => boolean,
): TilePoint[] {
  if (path.length <= 2) return path.map((p) => ({ x: p.x, y: p.y }));
  const result: TilePoint[] = [{ x: path[0]!.x, y: path[0]!.y }];
  let anchor = 0;
  for (let i = 2; i < path.length; i += 1) {
    const a = path[anchor]!;
    const c = path[i]!;
    // Nicht mehr direkt sichtbar -> vorheriger Punkt wird zum neuen Anker
    if (!isSegmentClear(a.x, a.y, c.x, c.y)) {
      const prev = path[i - 1]!;
      result.push({ x: prev.x, y: prev.y });
      anchor = i - 1;
    }
  }
  const last = path[path.length - 1]!;
  result.push({ x: last.x, y: last.y });
  return result;
}
